import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Mic, Video, Shuffle, ImagePlus, Sparkles, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { API } from "@/lib/api";
import { SiteFooter } from "@/components/legal/SiteFooter";

const HERO = "/hero.jpg";

const features = [
  { icon: MessageCircle, label: "Szöveg", color: "#FF3B6C" },
  { icon: Mic, label: "Mikrofon", color: "#00F0FF" },
  { icon: Video, label: "Webkamera", color: "#FFD600" },
  { icon: ImagePlus, label: "Kép & videó", color: "#00E676" },
];

const steps = [
  { n: "01", t: "Adj meg egy becenevet", d: "Nem kell regisztrálni. Ha nincs kedved, maradhatsz Ismeretlen.", c: "bg-[#FF3B6C]" },
  { n: "02", t: "Dobunk melléd valakit", d: "Teljesen véletlenszerű párosítás, bárkit kifoghatsz.", c: "bg-[#7C3AED]" },
  { n: "03", t: "Kapcsold be, amit szeretnél", d: "Mikrofon, kamera, képek – vagy csak írj. Unalmas? Következő!", c: "bg-[#00B8C4]" },
];

const useOnline = () => {
  const [n, setN] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/online`).then((r) => setN(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  return n;
};

const StartForm = () => {
  const navigate = useNavigate();
  const [nick, setNick] = useState(() => sessionStorage.getItem("esc_nick") || "");
  const start = (e) => {
    e.preventDefault();
    sessionStorage.setItem("esc_nick", nick.trim());
    navigate("/chat");
  };
  return (
    <form onSubmit={start} className="mt-10 flex w-full max-w-lg flex-col gap-3 sm:flex-row rise" style={{ animationDelay: ".3s" }}>
      <input data-testid="nickname-input" value={nick} onChange={(e) => setNick(e.target.value)} maxLength={24}
        placeholder="Becenév (opcionális)"
        className="flex-1 rounded-full border border-white/10 bg-white/5 px-6 py-4 text-base outline-none transition-colors placeholder:text-muted-foreground focus:border-[#FF3B6C]" />
      <button data-testid="start-chat-button" type="submit"
        className="group flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 px-8 py-4 font-display text-lg font-bold text-white shadow-[0_0_30px_rgba(255,59,108,0.4)] transition-transform hover:scale-[1.04] active:scale-95">
        Beszélgetés indítása <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
};

export default function Landing() {
  const online = useOnline();
  return (
    <div className="grain relative min-h-screen overflow-hidden">
      <div className="blob h-96 w-96 bg-[#FF3B6C] -left-24 top-10" />
      <div className="blob h-[28rem] w-[28rem] bg-[#7C3AED] right-[-8rem] top-40" style={{ animationDelay: "-5s" }} />
      <div className="blob h-72 w-72 bg-[#00F0FF] left-1/3 bottom-[-6rem] opacity-25" style={{ animationDelay: "-9s" }} />

      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-10">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="hidden rounded-full border border-[#FFD600]/40 px-3 py-1 text-xs font-bold text-[#FFD600] sm:inline">18+</span>
        </div>
      </header>

      <main className="relative z-10 mx-auto grid max-w-7xl gap-16 px-6 pb-24 pt-10 md:px-10 lg:grid-cols-[1.15fr_1fr] lg:pt-20">
        <section className="flex flex-col items-start">
          <div className="rise flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm" data-testid="online-counter">
            <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00E676] opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#00E676]" /></span>
            <span className="font-semibold">{online ?? "…"}</span> ember online most
          </div>
          <h1 className="rise mt-8 font-display text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: ".1s" }}>
            Beszélgess <span className="text-[#FF3B6C]">névtelenül</span><br />vadidegen <span className="relative inline-block text-[#00F0FF]">emberekkel<Sparkles className="absolute -right-8 -top-4 h-7 w-7 text-[#FFD600]" /></span>
          </h1>
          <p className="rise mt-6 max-w-lg text-base text-muted-foreground md:text-lg" style={{ animationDelay: ".2s" }}>
            Egy kattintás, és már bent is vagy. Írj, beszélj vagy kapcsold be a kamerát – ha nem jön be, egy gomb és jön a következő.
          </p>
          <StartForm />
          <div className="rise mt-8 flex flex-wrap gap-3" style={{ animationDelay: ".4s" }}>
            {features.map(({ icon: I, label, color }) => (
              <span key={label} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-semibold">
                <I className="h-4 w-4" style={{ color }} /> {label}
              </span>
            ))}
          </div>
        </section>

        <section className="rise relative hidden lg:block" style={{ animationDelay: ".25s" }}>
          <div className="relative rotate-[3deg] overflow-hidden rounded-[2rem] border-4 border-[#FF3B6C] shadow-[0_0_60px_rgba(255,59,108,0.35)]">
            <img src={HERO} alt="Videóchat" className="h-[440px] w-full object-cover" />
            <div className="absolute bottom-4 left-4 rounded-full bg-black/60 px-4 py-1.5 text-sm font-semibold backdrop-blur">Ismeretlen · élő</div>
          </div>
          <div className="absolute -bottom-8 -left-10 rotate-[-6deg] rounded-3xl bg-[#FFD600] px-6 py-4 font-display text-xl font-black text-[#0F0C1B] shadow-xl">Szia! Honnan írsz? 👋</div>
          <div className="absolute -right-4 -top-6 rotate-[8deg] rounded-full bg-[#00F0FF] p-4 text-[#0F0C1B] shadow-xl"><Shuffle className="h-7 w-7" /></div>
        </section>
      </main>

      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-28 md:px-10">
        <h2 className="mb-10 font-display text-2xl font-bold sm:text-3xl">Így működik</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={s.n} className={`rise rounded-3xl border border-white/10 bg-[#1A152E] p-8 transition-transform hover:-translate-y-2 ${i === 1 ? "md:translate-y-8" : ""}`} style={{ animationDelay: `${0.5 + i * 0.1}s` }}>
              <span className={`inline-block rounded-2xl ${s.c} px-3 py-1 font-display text-lg font-black text-white`}>{s.n}</span>
              <h3 className="mt-6 font-display text-xl font-bold">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-20 text-xs text-muted-foreground">Csak 18 év felettieknek. Légy kedves, ne ossz meg személyes adatot. A beszélgetéseket moderálás céljából 180 napig mentjük.</p>
        <SiteFooter />
      </section>
    </div>
  );
}
